import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Upload,
  FileText,
  X,
  ArrowLeft,
  FileCode,
} from 'lucide-react';
import { Button } from '@foundation/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@foundation/components/ui/card';
import { Input } from '@foundation/components/ui/input';
import { Textarea } from '@foundation/components/ui/textarea';
import { Label } from '@foundation/components/ui/label';
import { Progress } from '@foundation/components/ui/progress';
import { Alert, AlertDescription } from '@foundation/components/ui/alert';
import { useToast } from '@foundation/hooks/use-toast';
import ScopingAILayout from '../components/RevisrLayout';
import {
  parseDocument,
} from '../services/parseDocService';
import type { ParseDocProgress, ParseDocResult } from '../types/parseDoc';
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from '../lib/constants';

type AppStatus = 'upload' | 'processing' | 'result';

const ParseDoc: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [status, setStatus] = useState<AppStatus>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<ParseDocProgress | null>(null);
  const [result, setResult] = useState<ParseDocResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState<string>(
    `You are an information-extraction engine for retail electricity offers.

Your only task is to read the offer document text you are given and output a single JSON object in a very strict, predefined structure. The JSON structure must match the schema shown below. Ignore all parts of the document that are not needed to fill this JSON (e.g. standard terms and conditions, privacy, direct debit forms, execution blocks, marketing copy, etc.). Focus only on the sections that define pricing, non-energy charges, and environmental / certificate-related rates for the offers.

If you are not 100% sure a value is correct for a given field, leave that field as null rather than guessing or inferring.

You MUST:
1. Use only information explicitly present in the document (or derived by simple and clearly defined unit conversions as described below).
2. Never hallucinate or approximate values.
3. Prefer leaving fields as null over filling wrong values.
4. Return ONLY valid JSON. No comments, no explanations, no extra keys, no trailing commas, no text outside the JSON.

--------------------------------
INPUT
--------------------------------

You will receive:

- DOCUMENT_TEXT: the full text (OCR or parsed) of an electricity offer document. It may include:
  - A schedule or summary page listing one or more offers with energy rates (peak / off-peak / shoulder) by Agreement Year and target consumption (kWh).
  - Non-energy charges (e.g. metering, network, market, service charges).
  - Environmental products and rates tables (e.g. LREC, SREC, ESC/VEEC/ESS/REES) with certificate prices, environmental rates, and percentages by period/year.
  - Many pages of generic legal terms you should ignore.

Optionally:
- TARGET_OFFER_TYPE: one of "smooth" or "stepped."
- TARGET_OFFER_ID: an agreement/offer number (e.g. "3103419").

--------------------------------
OUTPUT SCHEMA
--------------------------------

You must produce a single JSON object matching this structure. 
Actual calendar-year keys may replace "year_1"…"year_6" if the document clearly shows real years AND you use them consistently across all sections.

{
  "smooth": {
    "peakRates": {
      "year_1": null,
      "year_2": null,
      "year_3": null,
      "year_4": null,
      "year_5": null,
      "year_6": null
    },
    "offpeakRates": {
      "year_1": null,
      "year_2": null,
      "year_3": null,
      "year_4": null,
      "year_5": null,
      "year_6": null
    },
    "shoulderRates": {
      "year_1": null,
      "year_2": null,
      "year_3": null,
      "year_4": null,
      "year_5": null,
      "year_6": null
    },
    "nonEnergyCharges": {
      "Market Fees": null,
      "Meter Agreement": null,
      "Network Service": null,
      "Supplementary Metering": null
    },
    "environmentalCharges": {
      "LREC": {
        "year_1": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_2": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_3": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_4": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_5": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_6": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null }
      },
      "SREC": {
        "year_1": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_2": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_3": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_4": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_5": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_6": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null }
      },
      "VEEC/ESS/REES": {
        "year_1": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_2": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_3": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_4": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_5": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_6": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null }
      }
    }
  },
  "stepped": {
    "peakRates": {
      "year_1": null,
      "year_2": null,
      "year_3": null,
      "year_4": null,
      "year_5": null,
      "year_6": null
    },
    "offpeakRates": {
      "year_1": null,
      "year_2": null,
      "year_3": null,
      "year_4": null,
      "year_5": null,
      "year_6": null
    },
    "shoulderRates": {
      "year_1": null,
      "year_2": null,
      "year_3": null,
      "year_4": null,
      "year_5": null,
      "year_6": null
    },
    "nonEnergyCharges": {
      "Market Fees": null,
      "Meter Agreement": null,
      "Network Service": null,
      "Supplementary Metering": null
    },
    "environmentalCharges": {
      "LREC": {
        "year_1": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_2": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_3": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_4": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_5": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_6": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null }
      },
      "SREC": {
        "year_1": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_2": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_3": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_4": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_5": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_6": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null }
      },
      "VEEC/ESS/REES": {
        "year_1": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_2": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_3": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_4": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_5": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null },
        "year_6": { "ratePerKwh": null, "powerPercentage": null, "pricePerCertificate": null }
      }
    }
  }
}

--------------------------------
YEAR RULES
--------------------------------

• You may use indexed keys ("year_1"…"year_6") or actual calendar years (e.g. "2025", "2026").  
• If using actual years, extract them directly from the document and use that format consistently across ALL sections.  
• Do not mix year_1/year_2 style and real-year style.  
• Do not fabricate years not explicitly present.

--------------------------------
OFFER SELECTION RULES
--------------------------------

• If TARGET_OFFER_TYPE is provided:
  – Populate only that branch (smooth or stepped); set the other branch to null.

• If TARGET_OFFER_ID is provided:
  – Use only the row matching that Agreement Number.

• If neither is provided:
  – If only one offer appears, treat it as smooth.
  – If multiple offers appear and they are not clearly labeled, set both smooth and stepped to null.

--------------------------------
ENERGY RATE RULES
--------------------------------

• Extract Peak, Off-Peak, Shoulder per agreement year.  
• Map agreement years chronologically to the chosen year keys.  
• If units are in c/kWh, you may keep them or convert to $/kWh ONLY when the unit is unambiguous.  
• Leave missing or unclear values as null.

--------------------------------
NON-ENERGY CHARGE RULES
--------------------------------

Map only if clearly labeled:
• “Market Fees”
• “Meter Agreement”
• “Network Service”
• “Supplementary Metering”

Use numeric value only; ambiguous → null.

--------------------------------
ENVIRONMENTAL CHARGE RULES
--------------------------------

Map only:
• LREC
• SREC
• VEEC/ESS/REES (combined)

For each year:
• pricePerCertificate → numeric only.  
• powerPercentage → numeric only (strip %).  
• ratePerKwh → convert $/MWh ÷ 1000 only if certain; $/kWh use as-is.  
• If uncertain, leave null.

Year keys must match the energy rate year keys.

--------------------------------
STRICTNESS
--------------------------------

• If uncertain: null.  
• If nothing is confidently extractable: output { "smooth": null, "stepped": null }.  
• No invented data, no assumptions.  
• No text outside the JSON.

--------------------------------
FINAL REQUIREMENTS
--------------------------------

1. Output a single valid JSON object following the schema.  
2. All numeric values must be JSON numbers.  
3. All missing or uncertain values must be null.  
4. No commentary, no explanation, no extra text before/after the JSON.`
  );

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return `File type ${file.type} is not supported. Please upload PDF, DOCX, TXT, or MD files.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum of ${MAX_FILE_SIZE / 1024 / 1024}MB`;
    }
    return null;
  };

  const handleFileSelect = (selectedFile: File): void => {
    const error = validateFile(selectedFile);
    if (error) {
      toast({
        title: 'Invalid File',
        description: error,
        variant: 'destructive',
      });
      return;
    }
    setFile(selectedFile);
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleParse = async (): Promise<void> => {
    if (!file) {
      toast({
        title: 'No File Selected',
        description: 'Please upload a document to parse',
        variant: 'destructive',
      });
      return;
    }

    setStatus('processing');
    const totalSteps = 1; // parse
    let currentStep = 0;

    try {
      // OpenRouter path: parse directly
      setProgress({
        current: currentStep++,
        total: totalSteps,
        message: 'Parsing document with OpenRouter...',
        fileName: file.name,
      });

      const parseResult = await parseDocument(systemPrompt, file);

      setResult(parseResult);
      setStatus('result');
      setProgress(null);

      toast({
        title: 'Document Parsed',
        description: parseResult.json ? 'Successfully extracted JSON data' : 'Parsed but JSON extraction failed',
        variant: parseResult.json ? 'default' : 'destructive',
      });
    } catch (error) {
      console.error('Error parsing document:', error);
      toast({
        title: 'Parsing Failed',
        description: error instanceof Error ? error.message : 'Failed to parse document',
        variant: 'destructive',
      });
      setStatus('upload');
      setProgress(null);
    }
  };

  const handleReset = (): void => {
    setFile(null);
    setResult(null);
    setStatus('upload');
    setProgress(null);
  };

  const renderUploadSection = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Parse Document</h1>
        <p className="text-muted-foreground">
          Upload a document to extract structured JSON data using AI. The document is processed with OpenRouter and automatically cleaned up after parsing.
        </p>
      </div>

      {/* File Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Document Upload
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? 'border-primary bg-primary/10'
                : 'border-muted-foreground/50'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {file ? (
              <div className="space-y-2">
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(file.size / 1024).toFixed(2)} KB
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFile(null)}
                >
                  <X className="h-4 w-4 mr-2" />
                  Remove
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium mb-1">
                    Drag & drop or click to upload
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PDF, DOCX, TXT, MD
                  </p>
                </div>
                <Input
                  type="file"
                  accept=".pdf,.docx,.doc,.txt,.md"
                  className="hidden"
                  id="file-upload"
                  onChange={(e) => {
                    const selectedFile = e.target.files?.[0];
                    if (selectedFile) handleFileSelect(selectedFile);
                  }}
                />
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  Select File
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* System Prompt Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCode className="h-5 w-5" />
            System Prompt Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="system-prompt">
              System Prompt (Instructions for AI)
            </Label>
            <Textarea
              id="system-prompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="Enter system prompt instructions..."
              className="min-h-[200px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              This prompt configures the AI to extract structured data from your document. The default prompt instructs the AI to return JSON format. Edit to customize the extraction behavior.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center">
        <Button
          onClick={handleParse}
          disabled={!file}
          size="lg"
        >
          <FileCode className="h-5 w-5 mr-2" />
          Parse Document
        </Button>
      </div>
    </div>
  );

  const renderProcessingSection = () => (
    <div className="flex flex-col items-center justify-center space-y-6 py-12">
      <Loader2 className="h-12 w-12 animate-spin text-primary" />
      {progress && (
        <div className="w-full max-w-md space-y-2">
          <p className="text-center font-medium">{progress.message}</p>
          {progress.fileName && (
            <p className="text-center text-sm text-muted-foreground">
              {progress.fileName}
            </p>
          )}
          <Progress
            value={(progress.current / progress.total) * 100}
            className="w-full"
          />
        </div>
      )}
    </div>
  );

  const renderResultSection = () => {
    if (!result) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Parse Results</h2>
          <Button variant="outline" onClick={handleReset}>
            <X className="h-4 w-4 mr-2" />
            Parse Another Document
          </Button>
        </div>

        {result.jsonError && (
          <Alert variant="destructive">
            <AlertDescription>
              <strong>JSON Parsing Error:</strong> {result.jsonError}
              <br />
              <span className="text-sm text-muted-foreground">
                Showing raw text output below.
              </span>
            </AlertDescription>
          </Alert>
        )}

        {result.json ? (
          <Card>
            <CardHeader>
              <CardTitle>Extracted JSON Data</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted p-4 rounded-md overflow-auto max-h-[600px] text-sm">
                {JSON.stringify(result.json, null, 2)}
              </pre>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Raw Text Output</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted p-4 rounded-md overflow-auto max-h-[600px] text-sm whitespace-pre-wrap">
                {result.text}
              </pre>
            </CardContent>
          </Card>
        )}

        {result.json && (
          <Card>
            <CardHeader>
              <CardTitle>Raw Text Output</CardTitle>
            </CardHeader>
            <CardContent>
              <details>
                <summary className="cursor-pointer text-sm font-medium mb-2">
                  View raw text output
                </summary>
                <pre className="bg-muted p-4 rounded-md overflow-auto max-h-[400px] text-sm whitespace-pre-wrap mt-2">
                  {result.text}
                </pre>
              </details>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <ScopingAILayout>
      <div className="container mx-auto px-4 py-5 md:py-6 space-y-5 md:space-y-6 overflow-x-hidden">
        <Button
          variant="ghost"
          onClick={() => navigate('/revisr')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        {status === 'upload' && renderUploadSection()}
        {status === 'processing' && renderProcessingSection()}
        {status === 'result' && renderResultSection()}
      </div>
    </ScopingAILayout>
  );
};

export default ParseDoc;

